import { useId } from 'react'

import { Icon } from '../../atoms/icon/icon'
import { Text } from '../../atoms/text/text'
import { FormField } from '../../molecules/form-field/form-field'
import { Input } from '../../molecules/input/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../molecules/select/select'
import { DatePickerPopover } from '../date-picker/date-picker-popover'

import type { OmniTableFilterDraft } from './omni-table-filters'

/* -------------------------------- amounts -------------------------------- */

export interface OmniTableAmountRange {
  min?: number
  max?: number
}

const parseAmount = (text: string): number | undefined => {
  if (text.trim() === '') return undefined
  const amount = Number(text)
  return Number.isFinite(amount) && amount >= 0 ? amount : undefined
}

/** `=250` is one exact amount; `100:500`, `100:` and `:500` are bounds. */
export function parseOmniTableAmountRange(
  value: readonly string[]
): OmniTableAmountRange {
  const [entry = ''] = value
  if (entry.startsWith('=')) {
    const exact = parseAmount(entry.slice(1))
    return exact === undefined ? {} : { min: exact, max: exact }
  }
  const [min = '', max = ''] = entry.split(':')
  return { min: parseAmount(min), max: parseAmount(max) }
}

export function serializeOmniTableAmountRange({
  min,
  max
}: OmniTableAmountRange): string[] {
  if (min === undefined && max === undefined) return []
  if (min !== undefined && min === max) return [`=${min}`]
  return [`${min ?? ''}:${max ?? ''}`]
}

export interface OmniTableAmountRangeContentProps extends OmniTableFilterDraft {
  /** The icon in each field. Defaults to the dollar sign. */
  currencyIcon?: string
}

const INVALID_AMOUNT = 'Enter a valid amount'

/** One exact amount, or a minimum and a maximum; the draft keeps the text as typed. */
export function OmniTableAmountRangeContent({
  value,
  onChange,
  currencyIcon = 'attach_money'
}: OmniTableAmountRangeContentProps) {
  const id = useId()
  const [entry = ''] = value
  const isExact = entry.startsWith('=')
  const exact = isExact ? entry.slice(1) : ''
  const [min = '', max = ''] = isExact ? [] : entry.split(':')
  const change = (next: string) =>
    onChange(next === '=' || next === ':' ? [] : [next])
  const invalid = (text: string) =>
    text !== '' && parseAmount(text) === undefined
  const minAmount = parseAmount(min)
  const maxAmount = parseAmount(max)
  const inverted =
    minAmount !== undefined && maxAmount !== undefined && minAmount > maxAmount
  const icon = (
    <Icon name={currencyIcon} aria-hidden className='text-surface-body' />
  )

  return (
    <div className='flex flex-col gap-4'>
      <FormField
        label='Amount'
        htmlFor={`${id}-amount`}
        error={invalid(exact)}
        errorMessage={INVALID_AMOUNT}
      >
        <Input
          id={`${id}-amount`}
          inputMode='decimal'
          placeholder='0.00'
          leadingIcon={icon}
          value={exact}
          onChange={event => change(`=${event.target.value}`)}
        />
      </FormField>
      <Text
        as='span'
        variant='caption-md'
        weight='semibold'
        className='text-surface-body'
      >
        OR
      </Text>
      <div className='grid grid-cols-2 gap-2'>
        <FormField
          label='Min'
          htmlFor={`${id}-min`}
          error={invalid(min)}
          errorMessage={INVALID_AMOUNT}
        >
          <Input
            id={`${id}-min`}
            inputMode='decimal'
            placeholder='0.00'
            leadingIcon={icon}
            value={min}
            onChange={event => change(`${event.target.value}:${max}`)}
          />
        </FormField>
        <FormField
          label='Max'
          htmlFor={`${id}-max`}
          error={invalid(max) || inverted}
          errorMessage={
            inverted ? 'Must be at least the minimum' : INVALID_AMOUNT
          }
        >
          <Input
            id={`${id}-max`}
            inputMode='decimal'
            placeholder='0.00'
            leadingIcon={icon}
            value={max}
            onChange={event => change(`${min}:${event.target.value}`)}
          />
        </FormField>
      </div>
    </div>
  )
}

/* --------------------------------- dates --------------------------------- */

// The Select and DatePicker triggers take the Input's md geometry here, so
// the panel's fields all share one box: 40px high with a 10px radius.
const RANGE_FIELD_CLASS_NAME = 'h-10 w-full rounded-[10px]'

export interface OmniTableDateRange {
  from?: Date
  to?: Date
  /** The chosen preset's id, when the bounds came from one. */
  preset?: string
}

export interface OmniTableDateRangePreset {
  id: string
  label: string
  /** The bounds the preset stands for, read when it is chosen or shown. */
  range: () => Pick<OmniTableDateRange, 'from' | 'to'>
}

const pad = (part: number) => String(part).padStart(2, '0')

/** Local calendar day, so the bounds do not shift with the timezone. */
const toDay = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

const parseDay = (text: string): Date | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
  if (!match) return undefined
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  )
  return date.getMonth() === Number(match[2]) - 1 ? date : undefined
}

/** A preset id, or `YYYY-MM-DD:YYYY-MM-DD` with either side optional. */
export function parseOmniTableDateRange(
  value: readonly string[],
  presets: readonly OmniTableDateRangePreset[] = []
): OmniTableDateRange {
  const [entry = ''] = value
  const preset = presets.find(item => item.id === entry)
  if (preset) return { ...preset.range(), preset: preset.id }
  const [from = '', to = ''] = entry.split(':')
  return { from: parseDay(from), to: parseDay(to) }
}

export function serializeOmniTableDateRange({
  from,
  to,
  preset
}: OmniTableDateRange): string[] {
  if (preset) return [preset]
  if (!from && !to) return []
  return [`${from ? toDay(from) : ''}:${to ? toDay(to) : ''}`]
}

export interface OmniTableDateRangeContentProps extends OmniTableFilterDraft {
  /** Named ranges offered above the bounds; choosing one fills them in. */
  presets?: readonly OmniTableDateRangePreset[]
  presetPlaceholder?: string
  minDate?: Date
  maxDate?: Date
}

/** A preset or explicit bounds; editing a bound turns a preset into explicit dates. */
export function OmniTableDateRangeContent({
  value,
  onChange,
  presets = [],
  presetPlaceholder = 'Select date range',
  minDate,
  maxDate
}: OmniTableDateRangeContentProps) {
  const id = useId()
  const range = parseOmniTableDateRange(value, presets)
  const setBounds = (from?: Date, to?: Date) =>
    onChange(serializeOmniTableDateRange({ from, to }))

  return (
    <div className='flex flex-col gap-4'>
      {presets.length > 0 && (
        <FormField label='Date range' htmlFor={`${id}-preset`}>
          <Select
            value={range.preset ?? null}
            // As a list, not a map: Base UI takes a map to label "no value"
            // itself and hides the placeholder.
            items={presets.map(preset => ({
              value: preset.id,
              label: preset.label
            }))}
            onValueChange={next => onChange(next ? [String(next)] : [])}
          >
            <SelectTrigger
              id={`${id}-preset`}
              className={RANGE_FIELD_CLASS_NAME}
            >
              <SelectValue placeholder={presetPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {presets.map(preset => (
                <SelectItem key={preset.id} value={preset.id}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      )}
      <div className='grid grid-cols-2 gap-2'>
        <FormField label='From' htmlFor={`${id}-from`}>
          <DatePickerPopover
            id={`${id}-from`}
            className={RANGE_FIELD_CLASS_NAME}
            value={range.from ?? null}
            minDate={minDate}
            maxDate={range.to ?? maxDate}
            placeholder='mm/dd/yyyy'
            onChange={from => setBounds(from, range.to)}
          />
        </FormField>
        <FormField label='To' htmlFor={`${id}-to`}>
          <DatePickerPopover
            id={`${id}-to`}
            className={RANGE_FIELD_CLASS_NAME}
            value={range.to ?? null}
            minDate={range.from ?? minDate}
            maxDate={maxDate}
            placeholder='mm/dd/yyyy'
            onChange={to => setBounds(range.from, to)}
          />
        </FormField>
      </div>
    </div>
  )
}
